import { Button, IconEye, Typography, Wrapper } from '@screentone/core';
import { format } from 'date-fns-tz';

import { WidePageWrapper } from 'components';
import { useDataModelContext } from 'contexts/datamodel/useDataModel';
import { BannerModuleContainer } from 'data/generated/graphql';
import styles from './BannerEdit.module.scss';
import BannerMultitabSection from './components/banner-multitab-section/BannerMultitabSection';
import PublishingButtons from './components/publishing-buttons/PublishingButtons';
import { useBannerPublish } from './hooks/useBannerPublish';

export const BannerEdit = () => {
  const { root, metadata, renderEntity, countContent, resetModelChanged } =
    useDataModelContext<BannerModuleContainer>();

  const {
    hasBannerChanged,
    isMutationInProgress,
    handlePublish,
    handlePreview,
    handleSchedule,
    handleReschedule,
    handleUpdateSchedule,
    handleCancelSchedule,
    showEditSchedule,
    hideEditSchedule,
    isInEditMode,
    savedForScheduleEdit,
    isRecentlyUpdated,
    isRecentlyPublish,
    hasRecentlyPublishedScheduledContent,
    setTabToChangeTo,
    soonToBePublishedScheduledContentPublishUtc,
    tabToChangeTo,
    isScheduling,
    isDeleting,
    isPublishing
  } = useBannerPublish();

  const handleScheduleClick = async (publishUtc: number) => {
    if (isInEditMode) {
      await handleReschedule(publishUtc);
    } else {
      await handleSchedule(publishUtc);
    }
  };

  const handleClickPublish = async () => {
    if (isInEditMode) {
      await handleUpdateSchedule();
    } else {
      await handlePublish();
    }

    resetModelChanged();
  };

  return (
    <WidePageWrapper padding={{ all: 'md' }}>
      <WidePageWrapper.Header>
        <Wrapper padding={{ top: 'md', bottom: 'md' }} className={styles.topPageHeader}>
          <div>
            <Typography variant="header2" margin={{ bottom: 'none' }} data-testid="banner-header-name">
              {metadata.name}
            </Typography>
          </div>
          <PublishingButtons
            soonToBePublishedScheduledContentPublishUtc={soonToBePublishedScheduledContentPublishUtc}
            hasBannerChanged={hasBannerChanged}
            isPerformingAction={isMutationInProgress}
            isInEditMode={isInEditMode}
            onPublish={handleClickPublish}
            onSchedule={handleScheduleClick}
            onCancelEdit={hideEditSchedule}
            onDelete={handleCancelSchedule}
            isScheduling={isScheduling}
            isDeleting={isDeleting}
            isPublishing={isPublishing}
          />
        </Wrapper>
      </WidePageWrapper.Header>
      <WidePageWrapper.Left>
        <Wrapper margin={{ bottom: 'sm' }} className={styles.moduleHeader}>
          <Wrapper>
            <Typography variant="header3" margin={{ bottom: 'none', top: 'mlg' }} data-testid="banner-title-item-count">
              {isInEditMode
                ? `Editing: ${format(metadata.publishUtc!, 'MMM d, yyyy h:mm aaa z')}`
                : `${countContent(root)}`}
            </Typography>
          </Wrapper>
          <Wrapper>
            <Button
              tertiary
              icon={IconEye}
              onClick={() => handlePreview()}
              disabled={isMutationInProgress}
              data-testid="banner-page-preview-button"
            >
              Preview
            </Button>
          </Wrapper>
        </Wrapper>
        <Wrapper className={styles.moduleBackground}>{renderEntity(root, { hierarchyId: '' })}</Wrapper>
      </WidePageWrapper.Left>
      <WidePageWrapper.Right>
        <BannerMultitabSection
          hasRecentlyPublishedScheduledContent={hasRecentlyPublishedScheduledContent}
          setTabToChangeTo={setTabToChangeTo}
          tabToChangeTo={tabToChangeTo}
          isInEditMode={isInEditMode}
          handleShowHistoryEditMode={showEditSchedule}
          isRecentlyUpdated={isRecentlyUpdated}
          isRecentlyPublished={isRecentlyPublish}
          savedForScheduleEdit={savedForScheduleEdit}
          handlePreview={handlePreview}
          isPerformingAction={isMutationInProgress}
        />
      </WidePageWrapper.Right>
    </WidePageWrapper>
  );
};
